import { PermissionGuard } from '@/features/auth';
import { useResetMfaMutation } from '../hooks/useResetMfaMutation';
import { useRevokeTrustedDevicesMutation } from '../hooks/useRevokeTrustedDevicesMutation';
import { useToggleMfaEnforcementMutation } from '../hooks/useToggleMfaEnforcementMutation';
import type { AdminUser } from '../types/users.types';
import { Alert, Button, Toggle } from '@/shared/components';

interface Props {
  userId: string;
  user: AdminUser;
}

export function UserMfaPanel({ userId, user }: Props) {
  const resetMutation = useResetMfaMutation();
  const revokeTrustedMutation = useRevokeTrustedDevicesMutation();
  const toggleEnforceMutation = useToggleMfaEnforcementMutation();

  const handleToggleEnforce = () => {
    toggleEnforceMutation.mutate({ id: userId, enforce: !user.mfa_enforced });
  };

  const handleResetMfa = () => {
    if (!window.confirm('Are you sure you want to reset MFA for this user? Their current MFA configuration will be removed.')) {
      return;
    }
    resetMutation.mutate(userId);
  };

  const handleRevokeTrusted = () => {
    if (!window.confirm('Are you sure you want to revoke all trusted devices? The user will need to re-verify on each device.')) {
      return;
    }
    revokeTrustedMutation.mutate(userId);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold mb-3">MFA Enforcement</h3>
        <div className="flex items-center gap-4 p-4 border border-mp-border rounded">
          <div className="flex-1">
            <p className="font-medium">Enforce MFA</p>
            <p id={`enforce-mfa-hint-${userId}`} className="text-sm text-mp-muted">
              When enabled, this user must use MFA to sign in.
            </p>
          </div>
          <PermissionGuard permission={{ resource: 'user', action: 'edit' }}>
            <Toggle
              id={`enforce-mfa-${userId}`}
              checked={user.mfa_enforced}
              onChange={handleToggleEnforce}
              disabled={toggleEnforceMutation.isPending}
              ariaLabel="Enforce MFA for this user"
              ariaDescribedBy={`enforce-mfa-hint-${userId}`}
            />
          </PermissionGuard>
        </div>
        {/* Errors surface via the hook-level handleUserMutationError toast — no inline alert. */}
        {toggleEnforceMutation.isSuccess && (
          <Alert variant="success" className="mt-2">
            <span>MFA enforcement updated.</span>
          </Alert>
        )}
      </div>

      <div>
        <h3 className="font-semibold mb-3">Reset MFA</h3>
        <div className="flex items-start gap-4 justify-between">
          <p className="text-sm text-mp-muted">
            Resetting MFA will remove the user's current MFA configuration. If Enforce MFA is on they will be asked to set up MFA again on next sign-in; if it is off they will sign in without MFA.
          </p>
          <PermissionGuard permission={{ resource: 'user', action: 'reset_mfa' }}>
            <Button
              size="sm"
              variant="warning"
              onClick={handleResetMfa}
              disabled={resetMutation.isPending}
              className="whitespace-nowrap"
            >
              Reset MFA
            </Button>
          </PermissionGuard>
        </div>
        {/* Errors surface via the hook-level handleUserMutationError toast — no inline alert. */}
        {resetMutation.isSuccess && (
          <Alert variant="success" className="mt-2">
            <span>MFA has been reset for this user.</span>
          </Alert>
        )}
      </div>

      <div>
        <h3 className="font-semibold mb-3">Trusted Devices</h3>
        <div className="flex items-center gap-4 justify-between">
          <p className="text-sm text-mp-muted">
            Revoking trusted devices will require the user to complete MFA verification on all their devices again.
          </p>
          <PermissionGuard permission={{ resource: 'user', action: 'reset_mfa' }}>
            <Button
              size="sm"
              variant="warning"
              onClick={handleRevokeTrusted}
              disabled={revokeTrustedMutation.isPending}
              className="whitespace-nowrap"
            >
              Revoke Trusted Devices
            </Button>
          </PermissionGuard>
        </div>
        {/* Errors surface via the hook-level handleUserMutationError toast — no inline alert. */}
        {revokeTrustedMutation.isSuccess && (
          <Alert variant="success" className="mt-2">
            <span>Trusted devices have been revoked.</span>
          </Alert>
        )}
      </div>
    </div>
  );
}
